import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ShoppingBag, CheckCircle2, Loader2, AlertCircle, Sparkles, Lock, RefreshCw, Layers, X, Plus } from "lucide-react";
import { toast } from "sonner";
import PackRequestModal from "@/components/PackRequestModal";

/**
 * Page Packs Métiers — catalogue de bibliothèques pré-remplies (one-shot Stripe).
 * Le webhook Stripe (backend) débloque automatiquement le pack après paiement
 * et importe les ouvrages dans la bibliothèque de l'entreprise.
 *
 * Bundle (option C user) :
 *   - 1 pack à la pièce : 58,80 € TTC (49 € HT)
 *   - 2 packs (Duo)     : 99 € TTC     (économie : 18,60 €)
 *   - 8 packs (Arsenal) : 349 € TTC    (économie : 121,40 €)
 *   - 3-7 packs         : pas d'offre, paiement à l'unité.
 */
const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

// Tarifs bundle alignés avec backend (routes/packs.py BUNDLE_PRICES_TTC)
const BUNDLE_PRICES = { 2: 99, 8: 349 };
const UNIT_PRICE = 58.80;
const UNIT_PRICE_HT = 49;

export default function Packs() {
  const [catalog, setCatalog] = useState([]);
  const [owned, setOwned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [buyingBundle, setBuyingBundle] = useState(false);
  const [refreshing, setRefreshing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  const [requestOpen, setRequestOpen] = useState(false);

  // Index des packs déjà débloqués (pack_id → meta)
  const ownedMap = useMemo(() => {
    const m = {};
    for (const p of owned) m[p.pack_id] = p;
    return m;
  }, [owned]);

  const reload = async () => {
    setLoading(true);
    try {
      const [cat, mine] = await Promise.all([
        api.get("/packs"),
        api.get("/packs/mine"),
      ]);
      setCatalog(cat.data.packs || []);
      setOwned(mine.data.packs || []);
    } catch {
      toast.error("Impossible de charger les packs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Toast post-paiement (success/cancel) — pack unique OU bundle
  useEffect(() => {
    const ok = searchParams.get("pack_purchased");
    const ko = searchParams.get("pack_cancelled");
    const bundleOk = searchParams.get("bundle_purchased");
    const bundleKo = searchParams.get("bundle_cancelled");
    if (ok || bundleOk) {
      const msg = bundleOk
        ? `🎉 Paiement validé — vos ${bundleOk} packs sont en cours de déblocage…`
        : "🎉 Paiement validé — votre pack est en cours de déblocage…";
      toast.success(msg);
      const t = setTimeout(reload, 3000);
      ["pack_purchased", "session_id", "bundle_purchased"].forEach((k) => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
      setSelected(new Set());
      return () => clearTimeout(t);
    }
    if (ko || bundleKo) {
      toast.info("Paiement annulé.");
      ["pack_cancelled", "bundle_cancelled"].forEach((k) => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ── Sélection (pour bundle) ───────────────────────────────────────────────
  const toggleSelect = (packId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(packId)) next.delete(packId); else next.add(packId);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const selectableIds = useMemo(
    () => catalog.filter((p) => !ownedMap[p.id]).map((p) => p.id),
    [catalog, ownedMap],
  );
  const selectAll = () => setSelected(new Set(selectableIds));

  // Logique bundle dynamique
  const sel = Array.from(selected).filter((id) => !ownedMap[id]); // ignore les déjà-owned au cas où
  const n = sel.length;
  const totalCatalogPacks = catalog.length;
  const isFullBundle = n === totalCatalogPacks && totalCatalogPacks === 8;
  const bundleEligible = n === 2 || isFullBundle;
  const bundlePrice = BUNDLE_PRICES[isFullBundle ? 8 : n] || null;
  const unitTotal = n * UNIT_PRICE;
  const savings = bundlePrice ? unitTotal - bundlePrice : 0;
  // ──────────────────────────────────────────────────────────────────────────

  const buy = async (packId) => {
    setBuying(packId);
    try {
      const { data } = await api.post(`/packs/checkout/${packId}`);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("URL Stripe manquante.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur lors de la création du paiement.";
      toast.error(detail);
    } finally {
      setBuying(null);
    }
  };

  const buyBundle = async () => {
    if (!bundleEligible) return;
    setBuyingBundle(true);
    try {
      const { data } = await api.post("/packs/checkout-bundle", { pack_ids: sel });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("URL Stripe manquante.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur lors de la création du paiement.";
      toast.error(detail);
    } finally {
      setBuyingBundle(false);
    }
  };

  const refreshPack = async (packId) => {
    setRefreshing(packId);
    try {
      const { data } = await api.post(`/packs/${packId}/refresh`);
      if ((data?.items_inserted || 0) === 0) {
        toast.info("Votre bibliothèque est déjà à jour pour ce pack 👍");
      } else {
        toast.success(`✨ ${data.items_inserted} nouveau(x) ouvrage(s) importé(s) dans votre bibliothèque !`);
      }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour du pack.");
    } finally {
      setRefreshing(null);
    }
  };

  return (
    <div data-testid="packs-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs mb-1 uppercase tracking-widest" style={{ color: "var(--pit-yellow)" }}>
            <Sparkles size={12} /> Packs Métiers
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight" style={{ color: "var(--chalk)" }}>
            Démarrez en <span style={{ color: "var(--pit-yellow)" }}>5 minutes</span>, pas en 5 semaines.
          </h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--ink-soft)" }}>
            Des bibliothèques d&apos;ouvrages pré-remplies par métier (prix HT indicatifs, temps réalistes,
            matériaux pré-renseignés). Payez une fois, importez en 1 clic dans votre bibliothèque PitBoard.
          </p>
        </div>
      </div>

      {/* Avertissement légal (Article 11.3 CGV) */}
      <div
        className="card p-3 flex items-start gap-3"
        style={{ background: "rgba(255,196,0,0.05)", borderColor: "rgba(255,196,0,0.30)" }}
        data-testid="packs-legal-notice"
      >
        <AlertCircle size={16} style={{ color: "var(--pit-yellow)" }} className="shrink-0 mt-0.5" />
        <div className="text-[12px] leading-relaxed" style={{ color: "var(--ink-soft)" }}>
          <b>Prix, temps, matériaux et consommations fournis à titre purement indicatif</b> (CGV Art. 11.3) — non
          contractuels. Adaptez systématiquement les tarifs, durées et quantités à votre zone géographique, votre
          clientèle et vos coûts réels avant émission de devis.
        </div>
      </div>

      {/* Bannière des offres bundle */}
      {!loading && selectableIds.length >= 2 && (
        <div
          className="card p-4 flex flex-wrap items-center justify-between gap-3"
          style={{
            background: "linear-gradient(135deg, rgba(225,6,0,0.10), rgba(255,196,0,0.05))",
            borderColor: "rgba(225,6,0,0.40)",
          }}
          data-testid="bundle-offers-banner"
        >
          <div className="flex items-start gap-3">
            <Layers size={20} style={{ color: "var(--board-red)" }} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-display text-base leading-tight" style={{ color: "var(--chalk)" }}>
                Offres groupées — cochez plusieurs packs ci-dessous
              </div>
              <div className="text-[12.5px] mt-1 flex flex-wrap gap-x-4 gap-y-1" style={{ color: "var(--ink-soft)" }}>
                <span><b style={{ color: "var(--pit-yellow)" }}>Duo (2 packs)</b> · 99 € TTC <span style={{ color: "var(--grey)" }}>(82,50 € HT · économie 18,60 €)</span></span>
                <span><b style={{ color: "var(--pit-yellow)" }}>Arsenal (8 packs)</b> · 349 € TTC <span style={{ color: "var(--grey)" }}>(290,83 € HT · économie 121,40 €, soit -26 %)</span></span>
                <span style={{ color: "var(--grey)" }}>· Sinon paiement à l&apos;unité (49 € HT / 58,80 € TTC / pack)</span>
              </div>
            </div>
          </div>
          {selectableIds.length === totalCatalogPacks && (
            <button
              onClick={selectAll}
              className="btn btn-outline"
              data-testid="bundle-select-all"
              style={{ fontSize: "0.78rem" }}
            >
              <Layers size={13} /> Tout cocher (Arsenal)
            </button>
          )}
        </div>
      )}

      {/* Catalogue */}
      {loading ? (
        <div className="card p-10 text-center" style={{ color: "var(--grey)" }}>
          <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Chargement…
        </div>
      ) : catalog.length === 0 ? (
        <div className="card p-10 text-center" style={{ color: "var(--grey)" }}>Aucun pack disponible.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {catalog.map((p) => {
            const o = ownedMap[p.id];
            const isOwned = Boolean(o);
            const isBuying = buying === p.id;
            const isSelected = selected.has(p.id);
            return (
              <div
                key={p.id}
                className="card p-5 flex flex-col gap-4 transition-transform hover:-translate-y-0.5"
                style={{
                  borderColor: isOwned
                    ? "rgba(16,185,129,0.45)"
                    : isSelected
                    ? "var(--board-red)"
                    : "var(--steel)",
                  boxShadow: isSelected ? "0 0 0 1px var(--board-red) inset" : undefined,
                }}
                data-testid={`pack-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3">
                    {!isOwned && (
                      <label
                        className="cursor-pointer flex items-center justify-center shrink-0 mt-1"
                        style={{
                          width: 22, height: 22,
                          borderRadius: 5,
                          border: `2px solid ${isSelected ? "var(--board-red)" : "var(--steel)"}`,
                          background: isSelected ? "var(--board-red)" : "transparent",
                          transition: "all 0.15s ease",
                        }}
                        data-testid={`pack-checkbox-${p.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(p.id)}
                          className="sr-only"
                        />
                        {isSelected && <CheckCircle2 size={14} style={{ color: "white" }} />}
                      </label>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--grey)" }}>
                        {p.trade}
                      </div>
                      <h3 className="font-display text-xl tracking-tight leading-tight" style={{ color: "var(--chalk)" }}>
                        {p.name}
                      </h3>
                      <p className="text-[13px] mt-1" style={{ color: "var(--ink-soft)" }}>{p.tagline}</p>
                    </div>
                  </div>
                  <ShoppingBag size={24} style={{ color: isOwned ? "rgb(16,185,129)" : "var(--pit-yellow)" }} />
                </div>

                <p className="text-[12px] leading-relaxed" style={{ color: "var(--grey)" }}>{p.description}</p>

                <ul className="space-y-1.5" data-testid={`pack-bullets-${p.id}`}>
                  {(p.bullets || []).map((b, i) => (
                    <li key={i} className="text-[13px] flex items-start gap-2" style={{ color: "var(--ink-soft)" }}>
                      <CheckCircle2 size={13} className="mt-0.5 shrink-0" style={{ color: "var(--pit-yellow)" }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-1 p-3 rounded-lg border flex items-baseline justify-between" style={{ background: "var(--carbon)", borderColor: "var(--steel)" }}>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Prix</div>
                    <div className="font-display text-2xl" style={{ color: "var(--chalk)" }}>
                      {fmtEUR(p.price_eur_ht ?? UNIT_PRICE_HT)} <span className="text-sm font-normal" style={{ color: "var(--ink-soft)" }}>HT</span>
                    </div>
                    <div className="text-[10px]" style={{ color: "var(--grey)" }}>Soit {fmtEUR(p.price_eur_ttc)} TTC · paiement unique</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Contenu</div>
                    <div className="font-display text-2xl" style={{ color: "var(--pit-yellow)" }}>{p.items_count}</div>
                    <div className="text-[10px]" style={{ color: "var(--grey)" }}>ouvrages</div>
                  </div>
                </div>

                {isOwned ? (
                  <div className="flex flex-col gap-2">
                    <button
                      className="btn w-full"
                      disabled
                      style={{
                        background: "rgba(16,185,129,0.15)",
                        borderColor: "rgba(16,185,129,0.45)",
                        color: "rgb(16,185,129)",
                        cursor: "default",
                        opacity: 1,
                      }}
                      data-testid={`pack-owned-${p.id}`}
                    >
                      <CheckCircle2 size={14} /> Débloqué · {o.items_inserted} ouvrages importés
                    </button>
                    <button
                      onClick={() => refreshPack(p.id)}
                      disabled={refreshing === p.id}
                      className="btn btn-outline w-full"
                      data-testid={`pack-refresh-${p.id}`}
                      title="Vérifier si de nouveaux ouvrages ont été ajoutés au pack depuis votre achat"
                    >
                      {refreshing === p.id ? (
                        <><Loader2 size={14} className="animate-spin" /> Mise à jour…</>
                      ) : (
                        <><RefreshCw size={14} /> Mettre à jour le pack</>
                      )}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => buy(p.id)}
                    disabled={isBuying}
                    className="btn btn-primary w-full"
                    data-testid={`pack-buy-${p.id}`}
                  >
                    {isBuying ? (
                      <><Loader2 size={14} className="animate-spin" /> Redirection Stripe…</>
                    ) : (
                      <><Lock size={14} /> Acheter {fmtEUR(p.price_eur_ht ?? UNIT_PRICE_HT)} HT</>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bloc "Demander un pack métier" */}
      {!loading && catalog.length > 0 && (
        <div
          className="mt-6 rounded-xl border-2 border-dashed p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderColor: "rgba(255,196,0,0.4)", background: "rgba(255,196,0,0.04)" }}
          data-testid="packs-request-block"
        >
          <div className="text-center sm:text-left">
            <h3 className="font-display text-xl mb-1" style={{ color: "var(--chalk)", fontWeight: 700 }}>
              Vous ne trouvez pas votre pack ?
            </h3>
            <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
              Dites-le nous — on peut l&apos;ajouter à la roadmap.
            </p>
          </div>
          <button
            onClick={() => setRequestOpen(true)}
            className="btn btn-primary shrink-0 inline-flex"
            style={{ minHeight: 44, padding: "0 18px", fontSize: "0.88rem", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}
            data-testid="packs-request-pack-btn"
          >
            <Plus size={14} /> Demander un pack
          </button>
        </div>
      )}

      {/* Barre flottante de sélection bundle */}
      {n > 0 && (
        <div
          className="fixed left-0 right-0 z-30 px-4"
          style={{ bottom: 16, pointerEvents: "none" }}
          data-testid="bundle-floating-bar"
        >
          <div
            className="max-w-3xl mx-auto rounded-xl border shadow-2xl flex items-center gap-3 p-3 pl-4"
            style={{
              background: "var(--carbon-2)",
              borderColor: bundleEligible ? "var(--board-red)" : "var(--steel)",
              backdropFilter: "blur(12px)",
              pointerEvents: "auto",
            }}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-display text-lg" style={{ color: "var(--chalk)" }}>
                  {n} pack{n > 1 ? "s" : ""} sélectionné{n > 1 ? "s" : ""}
                </span>
                {bundleEligible ? (
                  <span className="text-[12px] font-semibold" style={{ color: "var(--pit-yellow)" }}>
                    Bundle {isFullBundle ? "Arsenal" : "Duo"} · {fmtEUR(bundlePrice)} ({fmtEUR(savings)} d&apos;économie)
                  </span>
                ) : n === 1 ? (
                  <span className="text-[12px]" style={{ color: "var(--ink-soft)" }}>
                    Tarif unité {fmtEUR(unitTotal)}{" "}
                    <span style={{ color: "var(--grey)" }}>
                      · ajoute 1 pack → Bundle Duo {fmtEUR(BUNDLE_PRICES[2])}
                    </span>
                  </span>
                ) : (
                  <span className="text-[12px]" style={{ color: "var(--ink-soft)" }}>
                    Tarif unité {fmtEUR(unitTotal)}{" "}
                    <span style={{ color: "var(--grey)" }}>
                      · pas d&apos;offre groupée pour {n} packs
                      {totalCatalogPacks === 8 && (
                        <> — complète à 8 pour {fmtEUR(BUNDLE_PRICES[8])}</>
                      )}
                    </span>
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={clearSelection}
              className="btn btn-outline"
              style={{ minHeight: 38, padding: "0 10px" }}
              data-testid="bundle-clear-btn"
              title="Effacer la sélection"
            >
              <X size={14} />
            </button>
            {bundleEligible ? (
              <button
                onClick={buyBundle}
                disabled={buyingBundle}
                className="btn btn-primary"
                style={{ minHeight: 44, padding: "0 18px", fontWeight: 700 }}
                data-testid="bundle-buy-btn"
              >
                {buyingBundle ? (
                  <><Loader2 size={14} className="animate-spin" /> Redirection…</>
                ) : (
                  <><Lock size={14} /> Acheter le bundle {fmtEUR(bundlePrice)}</>
                )}
              </button>
            ) : null}
          </div>
        </div>
      )}

      <PackRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </div>
  );
}
